// client/src/components/ShopMap.js - Refactored with separated Init/Update Effects
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Alert, Spin, Typography } from 'antd';
import { initializeMap, loadLeaflet } from '../services/mapService';

const { Paragraph, Link: AntLink } = Typography;

const ShopMap = ({ shop }) => {
    const mapContainerDomRef = useRef(null);
    const mapInstanceRef = useRef(null);
    const markerRef = useRef(null);
    const [mapError, setMapError] = useState(null);
    const [isInitializing, setIsInitializing] = useState(false);
    const [mapContainerReady, setMapContainerReady] = useState(false);
    const [mapInitialized, setMapInitialized] = useState(false);

    const { coordinates, name, address, city, postalCode } = shop || {};
    const latitude = coordinates?.lat;
    const longitude = coordinates?.lng;

    // Ref Callback
    const mapContainerRefCallback = useCallback(node => {
        if (node !== null) {
            console.log("ShopMap: mapContainerRefCallback - Node attached.");
            mapContainerDomRef.current = node;
            setMapContainerReady(true);
        } else {
            console.log("ShopMap: mapContainerRefCallback - Node detached.");
            mapContainerDomRef.current = null;
            setMapContainerReady(false);
            setMapInitialized(false);
            if (mapInstanceRef.current) {
                try { mapInstanceRef.current.remove(); } catch (e) { /* ignore */ }
            }
            mapInstanceRef.current = null;
            markerRef.current = null;
        }
    }, []);

    // Effekt 1: Initialisiere Karte einmalig
    useEffect(() => {
        if (mapContainerReady && !mapInstanceRef.current && !isInitializing) {
            if (typeof latitude !== 'number' || typeof longitude !== 'number') {
                console.warn("ShopMap: Skipping initialization - invalid coordinates initially.");
                // Setze keinen Fehler hier, das Update-Hook wird es vielleicht später korrigieren
                // oder die Render-Logik zeigt eine Info an.
                return; 
            }

            const initMap = async () => {
                console.log('ShopMap: initMap - Attempting...');
                setIsInitializing(true);
                setMapError(null);
                let mapInstance = null; // Lokale Variable
                try {
                    const L = await loadLeaflet();
                    mapInstance = await initializeMap(mapContainerDomRef.current, {
                        center: [latitude, longitude],
                        zoom: 15
                    });
                    mapInstanceRef.current = mapInstance;

                    const marker = L.marker([latitude, longitude]).addTo(mapInstance);
                    markerRef.current = marker;

                    const popupContent = `
                        <div style="font-family: sans-serif; font-size: 13px;">
                            <h3 style="margin: 0 0 5px 0; font-size: 15px;">${name || 'Shop-Standort'}</h3>
                            ${address ? `<p style="margin: 0;">${address}, ${postalCode || ''} ${city || ''}</p>` : ''}
                        </div>
                    `;
                    marker.bindPopup(popupContent).openPopup();

                    setMapInitialized(true);
                    console.log('ShopMap: initMap - Success.');
                } catch (error) {
                    console.error('ShopMap: initMap - Error:', error);
                    setMapError(`Fehler beim Initialisieren der Karte: ${error.message}`);
                    if (mapInstance) { // Lokale Instanz entfernen bei Fehler
                        try { mapInstance.remove(); } catch (e) {}
                    }
                    mapInstanceRef.current = null; // Sicherstellen, dass Ref null ist
                    markerRef.current = null;
                    setMapInitialized(false);
                } finally {
                    setIsInitializing(false);
                }
            };
            initMap();
        }
        // Dieser Effekt soll wirklich nur einmal laufen, wenn der Container bereit ist.
        // Die Prüfung der Koordinaten passiert intern.
    }, [mapContainerReady]); // Abhängigkeit NUR von mapContainerReady

    // Effekt 2: Aktualisiere Kartenansicht und Marker, wenn sich Daten ändern (nach Init)
    useEffect(() => {
        // Nur ausführen, wenn Karte initialisiert UND Koordinaten gültig sind
        if (mapInitialized && mapInstanceRef.current && markerRef.current && typeof latitude === 'number' && typeof longitude === 'number') {
            console.log("ShopMap: UpdateEffect - Data changed, updating view and marker.");
            try {
                const newLatLng = [latitude, longitude];
                mapInstanceRef.current.setView(newLatLng, 15); 
                markerRef.current.setLatLng(newLatLng); 

                const popupContent = `
                    <div style="font-family: sans-serif; font-size: 13px;">
                        <h3 style="margin: 0 0 5px 0; font-size: 15px;">${name || 'Shop-Standort'}</h3>
                        ${address ? `<p style="margin: 0;">${address}, ${postalCode || ''} ${city || ''}</p>` : ''}
                    </div>
                `;
                if (markerRef.current.getPopup()) {
                     markerRef.current.getPopup().setContent(popupContent);
                } else {
                     markerRef.current.bindPopup(popupContent).openPopup();
                }
            } catch (error) {
                 console.error("ShopMap: UpdateEffect - Error updating map/marker:", error);
                 // Hier könnte man bei Bedarf einen Fehler setzen, aber vorsichtig sein, um Loops zu vermeiden
            }
        }
        // Wenn die Karte initialisiert ist, aber die Koordinaten ungültig werden,
        // könnte man hier optional die Karte entfernen oder eine Meldung anzeigen.
        else if (mapInitialized && mapInstanceRef.current && (typeof latitude !== 'number' || typeof longitude !== 'number')) {
             console.warn("ShopMap: UpdateEffect - Coordinates became invalid after initialization.");
             // Optional: Karte entfernen oder Fehler/Info setzen
             // setMapError("Koordinaten sind ungültig geworden.");
             // mapInstanceRef.current.remove(); mapInstanceRef.current = null; setMapInitialized(false);
        }
    }, [latitude, longitude, name, address, postalCode, city, mapInitialized]);

    // --- Render Logic ---
    const mapContainerStyle = { width: '100%', height: '300px' };

    // Render-Logik: Zeigt Fehler oder Info, wenn nötig
    const renderInfoOrError = () => {
        if (mapError) {
            const mapsLink = shop?.address ? `https://www.openstreetmap.org/search?query=${encodeURIComponent(shop.address + ", " + (shop.postalCode || '') + " " + (shop.city || ''))}` : null;
            return <Alert message={"Kartenfehler"} description={mapError} type="warning" showIcon action={mapsLink && <AntLink href={mapsLink} target="_blank" rel="noopener noreferrer">Auf OpenStreetMap suchen</AntLink>} />;
        }
        if (!isInitializing && !mapInitialized && (typeof latitude !== 'number' || typeof longitude !== 'number')) {
             const mapsLink = shop?.address ? `https://www.openstreetmap.org/search?query=${encodeURIComponent(shop.address + ", " + (shop.postalCode || '') + " " + (shop.city || ''))}` : null;
             return <Alert message={"Karte nicht verfügbar"} description={`Standortinformationen für ${name || 'diesen Shop'} sind unvollständig oder ungültig.`} type="info" showIcon action={mapsLink && <AntLink href={mapsLink} target="_blank" rel="noopener noreferrer">Auf OpenStreetMap suchen</AntLink>} />;
        }
        return null;
    };

    const infoOrError = renderInfoOrError();

    return (
        <div style={mapContainerStyle}>
            {/* Zeige Karte oder Info/Fehler */} 
            {infoOrError ? (
                <div style={{ padding: '20px' }}>{infoOrError}</div> // Padding für Alert
            ) : (
                <div ref={mapContainerRefCallback} style={{ width: '100%', height: '100%' }} />
            )}

            {/* Spinner Overlay über dem Container, wenn initialisiert wird */} 
            {isInitializing && (
                <div style={{ ...mapContainerStyle, position: 'absolute', top: 0, left: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(255,255,255,0.7)', zIndex: 10 }}>
                    <Spin tip="Karte wird initialisiert..." />
                </div>
            )}
        </div>
    );
};

// Nur ein Default Export
export default ShopMap;